/**
 * V5.8: 공휴일 추가/수정 모달
 *
 * 기능:
 * - 공휴일 추가/수정 폼
 * - national 타입 보호 정책:
 *   - 날짜, 유형 변경 불가 (readonly)
 *   - 이름만 수정 가능
 *   - 안내 메시지 표시
 *
 * V5.8 변경:
 * - Tailwind 클래스 제거, CSS 모듈로 전환
 */

import React, { useState, useEffect } from 'react';
import { X, Lock } from 'lucide-react';
import { useModalOverlayClose } from '../../../../shared/hooks/useModalOverlayClose';
import {
  Holiday,
  HolidayType,
  HOLIDAY_TYPE_LABELS,
  getDayOfWeekLabel,
} from './types';
import styles from './holidaysStyles.module.css';

interface HolidayFormModalProps {
  holiday: Holiday | null;
  onSave: (data: { date: string; name: string; type: HolidayType }) => void;
  onClose: () => void;
  isLoading: boolean;
}

export function HolidayFormModal({
  holiday,
  onSave,
  onClose,
  isLoading,
}: HolidayFormModalProps) {
  const [date, setDate] = useState(holiday?.date || '');
  const [name, setName] = useState(holiday?.name || '');
  const [type, setType] = useState<HolidayType>(holiday?.type || 'company');

  const isEditMode = !!holiday;
  const isNational = holiday?.type === 'national';

  // 수정 모드에서 값 초기화
  useEffect(() => {
    if (holiday) {
      setDate(holiday.date);
      setName(holiday.name);
      setType(holiday.type);
    } else {
      setDate('');
      setName('');
      setType('company');
    }
  }, [holiday]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!date || !name.trim()) {
      alert('날짜와 공휴일명을 입력해주세요.');
      return;
    }
    onSave({ date, name: name.trim(), type });
  };

  // ESC 키로 모달 닫기
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, isLoading]);

  const overlayProps = useModalOverlayClose(onClose);

  return (
    <div className={styles.modalOverlay} data-testid="holiday-form-modal-overlay" {...overlayProps}>
      <div
        className={styles.modalDialog}
        data-testid="holiday-form-modal"
      >
        {/* 헤더 */}
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            {isEditMode ? '공휴일 수정' : '공휴일 추가'}
          </h2>
          <button
            onClick={onClose}
            disabled={isLoading}
            className={styles.modalClose}
          >
            <X />
          </button>
        </div>

        {/* national 타입 안내 메시지 */}
        {isNational && (
          <div className={styles.formWarningBanner}>
            <Lock />
            <div className={styles.formWarningText}>
              <strong>법정공휴일 보호 정책</strong>
              <p>
                법정공휴일은 날짜와 유형을 변경할 수 없습니다.
                이름만 수정할 수 있습니다.
              </p>
            </div>
          </div>
        )}

        {/* 폼 */}
        <form onSubmit={handleSubmit} className={styles.formContainer}>
          {/* 날짜 */}
          <div className={styles.formField}>
            <label className={styles.formLabel}>
              날짜 <span className={styles.formLabelRequired}>*</span>
            </label>
            {isNational ? (
              <div className={styles.inputWrapper}>
                <input
                  type="text"
                  value={`${date} (${getDayOfWeekLabel(date)})`}
                  readOnly
                  className={styles.formInputReadonly}
                />
                <Lock />
              </div>
            ) : (
              <div className={styles.inputWrapper}>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className={styles.formInput}
                  required
                />
                {date && (
                  <span className={styles.dayOfWeekText}>
                    ({getDayOfWeekLabel(date)})
                  </span>
                )}
              </div>
            )}
          </div>

          {/* 공휴일명 */}
          <div className={styles.formField}>
            <label className={styles.formLabel}>
              공휴일명 <span className={styles.formLabelRequired}>*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="예: 신정, 설날 연휴"
              className={styles.formInputFull}
              required
              autoFocus={isNational}
            />
          </div>

          {/* 유형 */}
          <div className={styles.formField}>
            <label className={styles.formLabel}>
              유형 <span className={styles.formLabelRequired}>*</span>
            </label>
            {isNational ? (
              <div className={styles.inputWrapper}>
                <div className={styles.formReadonlyValue}>
                  <span className={styles.typeDot}></span>
                  {HOLIDAY_TYPE_LABELS.national}
                </div>
                <Lock />
              </div>
            ) : (
              <select
                value={type}
                onChange={(e) => setType(e.target.value as HolidayType)}
                className={styles.formSelect}
              >
                {(Object.keys(HOLIDAY_TYPE_LABELS) as HolidayType[]).map((t) => (
                  <option key={t} value={t}>
                    {HOLIDAY_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* 버튼 */}
          <div className={styles.formActions}>
            <button
              type="button"
              onClick={onClose}
              className={styles.btnSecondary}
              disabled={isLoading}
            >
              취소
            </button>
            <button
              type="submit"
              className={styles.btnPrimary}
              disabled={isLoading}
            >
              {isLoading ? '저장 중...' : '저장'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
